import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Flex, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { AssociationConfig, AssociationItem } from '../types';
import './AssociationLessonStudent.css';

const { Title, Text } = Typography;

// Helper function to get text by current language
const getTextByLanguage = (item: AssociationItem, currentLang?: string): string => {
  const lang = currentLang || (typeof window !== 'undefined' && localStorage.getItem('i18nextLng')) || 'en';
  const langKey = lang.startsWith('fr') ? 'fr' : lang.startsWith('ar') ? 'ar' : 'en';
  return item[`text_${langKey}` as keyof AssociationItem] as string || '';
};

// Helper function to get title by current language
const getTitleByLanguage = (config: AssociationConfig, side: 'left' | 'right', currentLang?: string): string => {
  const lang = currentLang || (typeof window !== 'undefined' && localStorage.getItem('i18nextLng')) || 'en';
  const langKey = lang.startsWith('fr') ? 'fr' : lang.startsWith('ar') ? 'ar' : 'en';
  const titleKey = `${side}Title_${langKey}` as keyof AssociationConfig;
  return (config[titleKey] as string) || '';
};

type Props = {
  config: AssociationConfig;
  statement?: string;
  onComplete?: () => void;
};

// Fisher-Yates shuffle algorithm
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const AssociationLessonStudent: React.FC<Props> = ({ config, statement, onComplete }) => {
  const { t, i18n } = useTranslation();
  const safeConfig = useMemo(
    () => ({ ...config, left: config.left || [], right: config.right || [] }),
    [config],
  );
  
  // Shuffle the arrays for random display order
  const shuffledLeft = useMemo(() => shuffleArray(safeConfig.left), [safeConfig.left]);
  const shuffledRight = useMemo(() => shuffleArray(safeConfig.right), [safeConfig.right]);
  
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [selectedLeft, setSelectedLeft] = useState<string | null>(null);

  // Refs for positioning and drawing lines
  const containerRef = useRef<HTMLDivElement | null>(null);
  const leftRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const rightRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [connections, setConnections] = useState<
    { 
      leftId: string; 
      rightKey: string; 
      x1: number; 
      y1: number; 
      x2: number; 
      y2: number; 
      dotX1: number;
      dotY1: number;
      dotX2: number;
      dotY2: number;
      controlX: number;
      controlY: number;
      correct: boolean;
      isNew?: boolean;
    }[]
  >([]);
  const [hoveredConnection, setHoveredConnection] = useState<string | null>(null);

  const allMatched = useMemo(() => {
    if (!shuffledLeft.length) return false;
    return shuffledLeft.every((item) => answers[item.id] && answers[item.id] === item.matchKey);
  }, [answers, shuffledLeft]);

  const handleSubmit = () => {
    setSubmitted(true);
    if (allMatched && onComplete) {
      onComplete();
    }
  };

  const handleLeftClick = (leftId: string) => {
    // Allow retry after submit if not all matched
    if (submitted && safeConfig.revealOnSubmit && allMatched) return;
    setSelectedLeft((prev) => (prev === leftId ? null : leftId));
  };

  const handleRightClick = (rightKey: string) => {
    // Allow retry after submit if not all matched
    if ((submitted && safeConfig.revealOnSubmit && allMatched) || !selectedLeft) return;
    setAnswers((prev) => {
      const newMap: Record<string, string> = { ...prev };
      // Ensure one-to-one: remove this rightKey from any other left
      Object.keys(newMap).forEach((k) => {
        if (newMap[k] === rightKey && k !== selectedLeft) {
          delete newMap[k];
        }
      });
      // Toggle if already mapped
      if (newMap[selectedLeft] === rightKey) {
        delete newMap[selectedLeft];
      } else {
        newMap[selectedLeft] = rightKey;
      }
      return newMap;
    });
  };

  const recomputeConnections = () => {
    const container = containerRef.current;
    if (!container) return;
    const cRect = container.getBoundingClientRect();
    const existingKeys = new Set(connections.map(c => `${c.leftId}-${c.rightKey}`));
    const DOT_OFFSET = 8; // Distance to place dots outside cards
    
    const conns: { 
      leftId: string; 
      rightKey: string; 
      x1: number; 
      y1: number; 
      x2: number; 
      y2: number; 
      dotX1: number;
      dotY1: number;
      dotX2: number;
      dotY2: number;
      controlX: number;
      controlY: number;
      correct: boolean;
      isNew?: boolean;
    }[] = [];
    
    Object.entries(answers).forEach(([leftId, rightKey]) => {
      const lEl = leftRefs.current[leftId];
      const rEl = rightRefs.current[rightKey];
      if (!lEl || !rEl) return;
      const lRect = lEl.getBoundingClientRect();
      const rRect = rEl.getBoundingClientRect();
      
      // Connection points at card edges
      const cardX1 = lRect.right - cRect.left;
      const cardY1 = lRect.top - cRect.top + lRect.height / 2;
      const cardX2 = rRect.left - cRect.left;
      const cardY2 = rRect.top - cRect.top + rRect.height / 2;
      
      // Dot positions outside the cards
      const dotX1 = cardX1 + DOT_OFFSET;
      const dotY1 = cardY1;
      const dotX2 = cardX2 - DOT_OFFSET;
      const dotY2 = cardY2;
      
      // Path coordinates
      const x1 = dotX1;
      const y1 = dotY1;
      const x2 = dotX2;
      const y2 = dotY2;
      
      // Calculate control point for smooth curve
      const controlX = (x1 + x2) / 2;
      const curveHeight = Math.min(Math.abs(y2 - y1) * 0.3, 40);
      const controlY = Math.min(y1, y2) - curveHeight;
      
      const leftItem = shuffledLeft.find((l) => l.id === leftId);
      const correct = !!leftItem && leftItem.matchKey === rightKey;
      const connectionKey = `${leftId}-${rightKey}`;
      const isNew = !existingKeys.has(connectionKey);
      
      conns.push({ 
        leftId, 
        rightKey, 
        x1, 
        y1, 
        x2, 
        y2,
        dotX1,
        dotY1,
        dotX2,
        dotY2,
        controlX,
        controlY,
        correct,
        isNew
      });
    });
    setConnections(conns);
  };

  useLayoutEffect(() => {
    recomputeConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    recomputeConnections();
    const timer = setTimeout(() => {
      setConnections(prev => prev.map(c => ({ ...c, isNew: false })));
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, submitted, shuffledLeft.length, shuffledRight.length]);

  useEffect(() => {
    const onResize = () => recomputeConnections();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="association-student">
      <Card>
        <Flex vertical gap={20}>
          {(statement || safeConfig.statement) && (
            <div className="association-statement">
              {statement || safeConfig.statement}
            </div>
          )}
          <div className="association-graph" ref={containerRef}>
            <svg className="association-lines">
              <defs>
                <marker
                  id="arrowhead-active"
                  markerWidth="12"
                  markerHeight="12"
                  refX="10"
                  refY="6"
                  orient="auto"
                  markerUnits="strokeWidth"
                >
                  <path
                    d="M 0 0 L 12 6 L 0 12 z"
                    fill="#2992bf"
                    opacity="0.9"
                  />
                </marker>
                <marker
                  id="arrowhead-correct"
                  markerWidth="12"
                  markerHeight="12"
                  refX="10"
                  refY="6"
                  orient="auto"
                  markerUnits="strokeWidth"
                >
                  <path
                    d="M 0 0 L 12 6 L 0 12 z"
                    fill="#8bbe1e"
                    opacity="0.9"
                  />
                </marker>
                <marker
                  id="arrowhead-incorrect"
                  markerWidth="12"
                  markerHeight="12"
                  refX="10"
                  refY="6"
                  orient="auto"
                  markerUnits="strokeWidth"
                >
                  <path
                    d="M 0 0 L 12 6 L 0 12 z"
                    fill="#ef4444"
                    opacity="0.9"
                  />
                </marker>
                <linearGradient id="connection-gradient-active" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#2992bf" stopOpacity="1" />
                  <stop offset="100%" stopColor="#8bbe1e" stopOpacity="1" />
                </linearGradient>
              </defs>
              {connections.map((c) => {
                const connectionKey = `${c.leftId}-${c.rightKey}`;
                const isHovered = hoveredConnection === connectionKey;
                const pathD = `M ${c.x1} ${c.y1} Q ${c.controlX} ${c.controlY} ${c.x2} ${c.y2}`;
                const strokeColor = submitted 
                  ? (c.correct ? '#8bbe1e' : '#ef4444')
                  : 'url(#connection-gradient-active)';
                const markerId = submitted
                  ? (c.correct ? 'arrowhead-correct' : 'arrowhead-incorrect')
                  : 'arrowhead-active';
                
                return (
                  <g key={connectionKey} className={`connection-group ${isHovered ? 'hovered' : ''} ${c.isNew ? 'new-connection' : ''} ${!submitted ? 'active' : ''}`}>
                    <path
                      d={pathD}
                      stroke="transparent"
                      strokeWidth="12"
                      fill="none"
                      className="association-path-hover"
                      onMouseEnter={() => setHoveredConnection(connectionKey)}
                      onMouseLeave={() => setHoveredConnection(null)}
                    />
                    <path
                      d={pathD}
                      stroke={strokeColor}
                      strokeWidth={isHovered ? 4 : 3}
                      fill="none"
                      markerEnd={`url(#${markerId})`}
                      className="association-path"
                    />
                    <circle
                      cx={c.dotX1}
                      cy={c.dotY1}
                      r={isHovered ? 7 : 6}
                      fill={submitted 
                        ? (c.correct ? '#8bbe1e' : '#ef4444')
                        : '#2992bf'}
                      className="connection-dot connection-dot-start"
                      stroke="white"
                      strokeWidth="2.5"
                      onMouseEnter={() => setHoveredConnection(connectionKey)}
                      onMouseLeave={() => setHoveredConnection(null)}
                    />
                    <circle
                      cx={c.dotX2}
                      cy={c.dotY2}
                      r={isHovered ? 7 : 6}
                      fill={submitted 
                        ? (c.correct ? '#8bbe1e' : '#ef4444')
                        : '#8bbe1e'}
                      className="connection-dot connection-dot-end"
                      stroke="white"
                      strokeWidth="2.5"
                      onMouseEnter={() => setHoveredConnection(connectionKey)}
                      onMouseLeave={() => setHoveredConnection(null)}
                    />
                  </g>
                );
              })}
            </svg>
            <Flex gap={16} align="stretch" className="association-columns">
              <div className="association-column">
                <Title level={5}>{getTitleByLanguage(safeConfig, 'left', i18n.language) || t('associationLesson.student.leftTitle') || 'Colonne A'}</Title>
                <Flex vertical gap={16}>
                {shuffledLeft.map((leftItem) => {
                  const isCorrect = submitted && answers[leftItem.id] === leftItem.matchKey;
                  const isWrong = submitted && answers[leftItem.id] && answers[leftItem.id] !== leftItem.matchKey;
                  const isSelected = selectedLeft === leftItem.id && !submitted;
                  return (
                    <Card
                      key={leftItem.id}
                      className={
                        isCorrect
                          ? 'association-card success'
                          : isWrong
                          ? 'association-card error'
                          : isSelected
                          ? 'association-card selected'
                          : 'association-card'
                      }
                      onClick={() => handleLeftClick(leftItem.id)}
                    >
                      <div
                        ref={(el) => {
                          leftRefs.current[leftItem.id] = el;
                        }}
                      >
                        <Flex vertical gap={8} align="center">
                          {leftItem.image && (
                            <div className="association-image-container">
                            <img
                              src={leftItem.image}
                              alt={getTextByLanguage(leftItem, i18n.language) || leftItem.id}
                              className="association-image"
                            />
                          </div>
                        )}
                        {getTextByLanguage(leftItem, i18n.language) && <Text strong>{getTextByLanguage(leftItem, i18n.language)}</Text>}
                        </Flex>
                      </div>
                    </Card>
                  );
                })}
                </Flex>
              </div>
              <div className="association-column">
                <Title level={5}>{getTitleByLanguage(safeConfig, 'right', i18n.language) || t('associationLesson.student.rightTitle') || 'Colonne B'}</Title>
                <Flex vertical gap={16}>
                {shuffledRight.map((item) => {
                  const isCorrectRight = submitted && Object.entries(answers).some(
                    ([leftId, rightKey]) => 
                      rightKey === item.matchKey && 
                      shuffledLeft.find(l => l.id === leftId)?.matchKey === rightKey
                  );
                  const isWrongRight = submitted && Object.entries(answers).some(
                    ([leftId, rightKey]) => 
                      rightKey === item.matchKey && 
                      shuffledLeft.find(l => l.id === leftId)?.matchKey !== rightKey
                  );
                  return (
                  <Card
                    key={item.id}
                    className={
                      isCorrectRight
                        ? 'association-card success'
                        : isWrongRight
                        ? 'association-card error'
                        : 'association-card muted'
                    }
                    onClick={() => handleRightClick(item.matchKey)}
                  >
                    <div
                      ref={(el) => {
                        rightRefs.current[item.matchKey] = el;
                      }}
                    >
                      <Flex vertical gap={8} align="center">
                        {item.image && (
                          <div className="association-image-container">
                            <img
                              src={item.image}
                              alt={getTextByLanguage(item, i18n.language) || item.id}
                              className="association-image"
                            />
                          </div>
                        )}
                        {getTextByLanguage(item, i18n.language) && <Text>{getTextByLanguage(item, i18n.language)}</Text>}
                      </Flex>
                    </div>
                  </Card>
                  );
                })}
                </Flex>
              </div>
            </Flex>
          </div>
          <Button type="primary" onClick={handleSubmit}>
            {t('associationLesson.student.submit') || 'Valider'}
          </Button>
          {submitted && !allMatched && (
            <Alert
              type="warning"
              showIcon
              message={t('associationLesson.student.tryAgain') || 'Certaines associations sont incorrectes.'}
            />
          )}
          {submitted && allMatched && (
            <Alert
              type="success"
              showIcon
              message={t('associationLesson.student.completed') || 'Toutes les associations sont correctes !'}
            />
          )}
        </Flex>
      </Card>
    </div>
  );
};

export default AssociationLessonStudent;
